import { Component } from '@theme/component';
import { fetchConfig, getCartJson, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * @param {Array<[string, string]> | Record<string, string> | null | undefined} properties
 * @returns {Record<string, string>}
 */
function normalizeLineItemProperties(properties) {
  if (!properties) return {};

  if (Array.isArray(properties)) {
    /** @type {Record<string, string>} */
    const normalized = {};
    for (const entry of properties) {
      if (Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string') {
        normalized[entry[0]] = entry[1];
      }
    }
    return normalized;
  }

  return properties;
}

/**
 * Whether a cart line was added by a Rebuy gift-with-purchase widget. Such a line
 * must not lend its properties to a customer add: copying `_widget_id`,
 * `_r_gwp_hide_qty` and friends would merge the add into the gift line instead
 * of creating a separate, paid line.
 *
 * @param {Array<[string, string]> | Record<string, string> | null | undefined} properties
 * @returns {boolean}
 */
function isRebuyGiftLineProperties(properties) {
  const normalized = normalizeLineItemProperties(properties);

  if (normalized._r_gwp_hide_qty != null) return true;
  return /gift with purchase/i.test(String(normalized._attribution ?? ''));
}

/**
 * Reads a theme translation with an English fallback, so a storefront missing a
 * key still shows something actionable instead of "undefined".
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function getTranslation(key, fallback) {
  const value = /** @type {Record<string, string>} */ (Theme.translations ?? {})[key];
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

/**
 * Sets the message on `.product-form-text__error` without removing the icon markup.
 *
 * @param {HTMLElement | undefined} errorElement
 * @param {string} message
 */
function setAddToCartErrorMessage(errorElement, message) {
  if (!errorElement) return;

  const textNode = errorElement.childNodes[2];
  if (textNode?.nodeType === Node.TEXT_NODE) {
    textNode.textContent = message;
    return;
  }

  errorElement.appendChild(document.createTextNode(message));
}

/**
 * An error whose message is already customer facing, so the catch block shows it
 * verbatim rather than replacing it with a generic message.
 */
class CartEditError extends Error {}

/**
 * Checks whether a selling plan can still be applied to a variant.
 *
 * Replacing a line is destructive, and Shopify silently drops a selling plan
 * that does not exist for the variant being added, turning a subscription into
 * a one time purchase. Unverifiable is treated the same as unavailable.
 *
 * @param {string} productUrl - Product page URL for the selected variant.
 * @param {string} variantId - The variant being added.
 * @param {string | number} sellingPlanId - The selling plan on the original line.
 * @returns {Promise<boolean>}
 */
async function isSellingPlanAvailableForVariant(productUrl, variantId, sellingPlanId) {
  if (!productUrl) return false;

  try {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/+$/, '')}.js`;

    const response = await fetch(url.toString());
    if (!response.ok) return false;

    const product = await response.json();
    const variant = product?.variants?.find(
      (/** @type {{ id: number | string }} */ candidate) => String(candidate.id) === String(variantId)
    );

    if (!variant) return false;

    return Boolean(
      variant.selling_plan_allocations?.some(
        (/** @type {{ selling_plan_id: number | string }} */ allocation) =>
          String(allocation.selling_plan_id) === String(sellingPlanId)
      )
    );
  } catch (error) {
    console.error('[ProductForm] Failed to verify the selling plan for the selected variant:', error);
    return false;
  }
}

/**
 * Builds the `/cart/add` body for a cart line, preserving the pieces Shopify
 * keys a line on.
 *
 * @param {{ variantId: string | number, quantity: number, properties: Record<string, string>, sellingPlanId?: string | number }} line
 * @param {string} [sections]
 * @returns {FormData}
 */
function buildCartAddBody(line, sections) {
  const body = new FormData();

  body.set('id', String(line.variantId));
  body.set('quantity', String(line.quantity));

  for (const [key, value] of Object.entries(line.properties)) {
    if (value == null || value === '') continue;
    body.set(`properties[${key}]`, String(value));
  }

  if (line.sellingPlanId) {
    body.set('selling_plan', String(line.sellingPlanId));
  }

  if (sections) {
    body.set('sections', sections);
  }

  return body;
}

/**
 * Whether a cart line has the identity Shopify merges lines on: variant,
 * selling plan and line item properties.
 *
 * @param {any} line
 * @param {{ variantId: string | number, properties: Record<string, string>, sellingPlanId?: string | number }} identity
 * @returns {boolean}
 */
function matchesCartLineIdentity(line, identity) {
  if (String(line?.variant_id) !== String(identity.variantId)) return false;

  const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
  if (String(sellingPlanId ?? '') !== String(identity.sellingPlanId ?? '')) return false;

  const properties = normalizeLineItemProperties(line.properties);
  const keys = Object.keys(properties);

  return (
    keys.length === Object.keys(identity.properties).length &&
    keys.every((key) => String(properties[key]) === String(identity.properties[key]))
  );
}

/**
 * Finds a line in a cart response by its key, falling back to its identity.
 * Shopify can issue a new key when a line's quantity changes (notably while a
 * cart level automatic discount applies), so a successful update may come back
 * under a different key. An ambiguous identity match is treated as not found.
 *
 * @param {any[] | undefined} items
 * @param {string} key
 * @param {{ variantId: string | number, properties: Record<string, string>, sellingPlanId?: string | number }} identity
 * @returns {any}
 */
function findCartLine(items, key, identity) {
  if (!Array.isArray(items)) return undefined;

  const keyedLine = items.find((item) => item.key === key);
  if (keyedLine) return keyedLine;

  const matches = items.filter((item) => matchesCartLineIdentity(item, identity));
  return matches.length === 1 ? matches[0] : undefined;
}

/**
 * Reads the cart line being edited by its exact key. The theme's JSON cart view
 * is rendered by Liquid, whose keys can drift from the Ajax API's after a
 * quantity change. Rebuy takes its keys from the Ajax API, so a key the view
 * does not know is looked up in `/cart.js` before giving up.
 *
 * @param {string} cartLineKey
 * @returns {Promise<any>}
 */
async function findCartLineByKey(cartLineKey) {
  const cart = await getCartJson();
  const line = cart.items?.find((/** @type {{ key?: string }} */ item) => item.key === cartLineKey);
  if (line) return line;

  const response = await fetch(`${Theme.routes.cart_url}.js`);
  if (!response.ok) return undefined;

  const ajaxCart = await response.json();
  return ajaxCart.items?.find((/** @type {{ key?: string }} */ item) => item.key === cartLineKey);
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  return sectionIds;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const isQuickAddDirect = this.refs.addToCartButton.classList.contains('quick-add__button--add');

    if (isQuickAddDirect) {
      this.startLoading();
    } else {
      this.animateAddToCart();
    }

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  /**
   * Function for loading state anims to quick add button.
   */
  startLoading() {
    const { addToCartButton } = this.refs;

    if (!addToCartButton) return;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    addToCartButton.classList.remove('atc-added');
    addToCartButton.classList.add('atc-loading');
    addToCartButton.setAttribute('aria-busy', 'true');
  }

  /**
   * Hides the in-button loading spinner.
   */
  stopLoading() {
    const { addToCartButton } = this.refs;

    if (!addToCartButton) return;

    addToCartButton.classList.remove('atc-loading');
    addToCartButton.removeAttribute('aria-busy');
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * Syncs line item properties from the live cart before posting so that
   * re-adding a variant that is already in the cart (with properties such as a
   * royalty code) increments its quantity rather than creating a duplicate entry.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    // Any shell can own a cart line edit by opting into `data-cart-edit-context`:
    // the global Quick Add dialog used by the cart page, and the Rebuy variant
    // edit drawer.
    const cartEditOwner = this.closest('[data-cart-edit-context]');
    if (cartEditOwner instanceof HTMLElement && cartEditOwner.dataset.cartLineKey) {
      await this.#replaceCartLine(event, cartEditOwner);
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const isQuickAddDirect = addToCartButton?.classList.contains('quick-add__button--add');

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButton?.getAttribute('disabled') === 'true') return;

    if (isQuickAddDirect) {
      if (this.#submitting) return;
      this.#submitting = true;
      this.refs.addToCartButtonContainer?.startLoading();
    }

    await this.#syncCartProperties();

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) {
      if (isQuickAddDirect) {
        this.#submitting = false;
        this.refs.addToCartButtonContainer?.stopLoading();
      }
      throw new Error('Product form element missing');
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          setAddToCartErrorMessage(addToCartTextError, response.message);

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (isQuickAddDirect) {
            this.refs.addToCartButtonContainer?.animateAddToCart();
          }

          // Add aria-live region to inform screen readers that the item was added
          if (addToCartButton) {
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              addedItems: response.items || [],
              analyticsModule: this.dataset.analyticsModule || 'product-form',
              redirectUrl: this.dataset.redirectUrl,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (isQuickAddDirect) {
          this.#submitting = false;
          this.refs.addToCartButtonContainer?.stopLoading();
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates an exact cart line when the owning shell is in cart edit context.
   *
   * Quantity-only edits update the keyed line directly. Shopify's Ajax Cart API
   * cannot move a line from one variant to another, so variant edits remove the
   * keyed line and add the selected variant at the requested quantity. That is
   * destructive: everything needed to put the original line back is captured
   * first, and restored if the add fails. The original line is always addressed
   * by its exact key, never by variant ID alone, because identical variants can
   * exist as separate lines through properties or selling plans.
   *
   * @param {Event} event
   * @param {HTMLElement} cartEditOwner - The element carrying `data-cart-edit-context`.
   */
  async #replaceCartLine(event, cartEditOwner) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    const addToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (addToCartButton?.getAttribute('disabled') === 'true') return;
    if (this.#submitting) return;

    const cartLineKey = cartEditOwner.dataset.cartLineKey;
    const cartVariantId = cartEditOwner.dataset.cartVariantId;
    const cartQuantity = Number(cartEditOwner.dataset.cartQuantity) || 1;
    const isRebuyEdit = cartEditOwner.dataset.cartEditSource === 'rebuy';
    const newVariantId = this.refs.variantId?.value;

    if (!cartLineKey || !newVariantId) return;

    this.#submitting = true;
    addToCartButtonContainer?.startLoading();

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('replace:user-action');

    /** @type {{ variantId: string | number, quantity: number, properties: Record<string, string>, sellingPlanId?: string | number } | null} */
    let originalLine = null;
    /** Set between the removal and a confirmed add, while the cart is missing a line. */
    let needsRestore = false;
    /** Quantity to report if the operation fails after reading the canonical cart. */
    let failureItemCount = cartQuantity;

    try {
      const lineItem = await findCartLineByKey(cartLineKey);

      if (!lineItem) {
        throw new Error('Cart line item not found');
      }

      const properties = normalizeLineItemProperties(lineItem.properties);
      const sellingPlanId = lineItem.selling_plan_allocation?.selling_plan?.id;
      const currentQuantity = Number(lineItem.quantity) || cartQuantity;
      const form = this.querySelector('form');
      // Cart edit shells seed the form with the line's quantity when they open
      // (`applyCartEditQuantity`), so the submitted quantity is the shopper's.
      const submittedQuantity = Number(form ? new FormData(form).get('quantity') : NaN);
      const quantity =
        Number.isSafeInteger(submittedQuantity) && submittedQuantity > 0 ? submittedQuantity : currentQuantity;
      const currentVariantId = lineItem.variant_id ?? cartVariantId;
      const variantChanged = String(newVariantId) !== String(currentVariantId);
      const quantityChanged = quantity !== currentQuantity;

      failureItemCount = currentQuantity;

      originalLine = {
        variantId: currentVariantId,
        quantity: currentQuantity,
        properties,
        sellingPlanId,
      };

      if (!variantChanged && !quantityChanged) {
        await this.#closeCartEditOwner(cartEditOwner);
        return;
      }

      const sections = getCartSectionIds().join(',');
      let responseSections;
      let addedItems = [];

      if (!variantChanged) {
        // Quantity-only edits can update the keyed line directly. Avoiding a
        // remove/add cycle preserves its identity, properties and selling plan.
        const changeResponse = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({
              id: cartLineKey,
              quantity,
              sections,
              sections_url: window.location.pathname,
            }),
          })
        );
        const changeResult = await changeResponse.json();

        if (!changeResponse.ok || changeResult.status || changeResult.errors) {
          throw new CartEditError(
            changeResult.message ||
              changeResult.description ||
              changeResult.errors ||
              'Failed to update cart line item'
          );
        }

        const updatedLine = findCartLine(changeResult.items, cartLineKey, originalLine);

        if (!updatedLine || Number(updatedLine.quantity) !== quantity) {
          throw new CartEditError(
            getTranslation(
              'cart_edit_recovery_error',
              "We couldn't confirm this change. Please review your cart before checking out."
            )
          );
        }

        responseSections = changeResult.sections;
      } else {
        // Checked before anything destructive: a subscription the selected variant
        // cannot carry would otherwise be silently dropped by /cart/add.
        if (sellingPlanId) {
          const sellingPlanAvailable = await isSellingPlanAvailableForVariant(
            this.#getCartEditProductUrl(cartEditOwner),
            newVariantId,
            sellingPlanId
          );

          if (!sellingPlanAvailable) {
            throw new CartEditError(
              getTranslation(
                'cart_edit_selling_plan_error',
                "This option isn't available for your current subscription. Please choose a different option."
              )
            );
          }
        }

        const removeResponse = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({
              id: cartLineKey,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          })
        );
        const removeResult = await removeResponse.json();

        if (!removeResponse.ok || removeResult.status || removeResult.errors) {
          throw new Error(
            removeResult.message || removeResult.description || removeResult.errors || 'Failed to update cart line item'
          );
        }

        needsRestore = true;

        const addBody = buildCartAddBody({ variantId: newVariantId, quantity, properties, sellingPlanId }, sections);
        const fetchCfg = fetchConfig('javascript', { body: addBody });
        const addResponse = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        });
        const addResult = await addResponse.json();

        if (addResult.status) {
          throw new CartEditError(addResult.message || addResult.description || 'Failed to add the selected variant');
        }

        // The add reported success, so restoring now would duplicate the line.
        needsRestore = false;

        // "Updated" is only honest once the settled cart actually carries the
        // selected variant.
        const settledCart = await getCartJson();
        const settledLine = settledCart.items?.find(
          (/** @type {{ variant_id?: number | string }} */ item) => String(item.variant_id) === String(newVariantId)
        );

        if (!settledLine) {
          throw new CartEditError(
            getTranslation(
              'cart_edit_recovery_error',
              "We couldn't confirm this change. Please review your cart before checking out."
            )
          );
        }

        responseSections = addResult.sections ?? removeResult.sections;
        addedItems = addResult.items || [];
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      addToCartButtonContainer?.animateAddToCart();

      if (addToCartButton) {
        this.#setLiveRegionText(getTranslation('updated', 'Updated'));

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      this.dispatchEvent(
        new CartAddEvent({}, newVariantId, {
          source: isRebuyEdit ? 'rebuy' : 'product-form-component',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: responseSections,
          addedItems,
          analyticsModule: isRebuyEdit ? 'rebuy' : this.dataset.analyticsModule || 'product-form',
          redirectUrl: this.dataset.redirectUrl,
        })
      );
    } catch (error) {
      console.error(error);

      let message =
        error instanceof CartEditError
          ? error.message
          : getTranslation('cart_edit_error', "We couldn't update this item. Please try again.");

      // The original line was removed and never replaced, so put it back.
      if (needsRestore && originalLine) {
        const restored = await this.#restoreCartLine(originalLine);

        if (!restored) {
          message = getTranslation(
            'cart_edit_recovery_error',
            "We couldn't confirm this change. Please review your cart before checking out."
          );
        }
      }

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        setAddToCartErrorMessage(addToCartTextError, message);
      }

      this.#setLiveRegionText(message);

      window.dispatchEvent(new CartErrorEvent(this.id, message));

      // Keeps the owning shell open, and keeps analytics from recording a
      // replacement that did not happen.
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          didError: true,
          source: isRebuyEdit ? 'rebuy' : 'product-form-component',
          itemCount: failureItemCount,
          productId: this.dataset.productId,
        })
      );
    } finally {
      this.#submitting = false;
      addToCartButtonContainer?.stopLoading();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Adds the original line back after a failed replacement. Shopify keys a line
   * on variant, properties and selling plan, so re-adding the same three
   * restores the same key.
   *
   * @param {{ variantId: string | number, quantity: number, properties: Record<string, string>, sellingPlanId?: string | number }} originalLine
   * @returns {Promise<boolean>} False when the cart is left without the line.
   */
  async #restoreCartLine(originalLine) {
    try {
      const fetchCfg = fetchConfig('javascript', { body: buildCartAddBody(originalLine) });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const result = await response.json();

      return response.ok && !result.status;
    } catch (error) {
      console.error('[ProductForm] Failed to restore the original cart line:', error);
      return false;
    }
  }

  /**
   * The product URL for the variant currently selected in the editor. The live
   * variant picker wins over the URL the editor opened with, because a combined
   * listing can move the selection to a different product.
   *
   * @param {HTMLElement} cartEditOwner
   * @returns {string}
   */
  #getCartEditProductUrl(cartEditOwner) {
    const variantPicker = /** @type {HTMLElement | null} */ (
      cartEditOwner.querySelector('variant-picker[data-product-url]')
    );

    return variantPicker?.dataset.productUrl || cartEditOwner.dataset.cartProductUrl || '';
  }

  /**
   * @param {HTMLElement} cartEditOwner
   */
  async #closeCartEditOwner(cartEditOwner) {
    const owner = /** @type {any} */ (cartEditOwner);

    if (typeof owner.closeDialog === 'function') {
      await owner.closeDialog();
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Remove all `data-cart-property` hidden inputs previously injected into the
   * form. Called before each re-injection to prevent stale or duplicate values
   * from accumulating across submissions.
   */
  #clearCartPropertyInputs() {
    const form = this.querySelector('form');
    if (!form) return;
    form.querySelectorAll('input[data-cart-property]').forEach((input) => input.remove());
  }

  /**
   * Fetch the live cart and inject hidden `properties[key]` inputs into the form
   * for the line item that matches the currently selected variant.
   *
   * Shopify identifies cart line items by the combination of variant ID *and*
   * properties. Without forwarding the existing properties on re-add, Shopify
   * treats the submission as a brand-new line item, causing duplicate entries.
   *
   * Runs just before the form is submitted so it always reflects the latest
   * cart state without needing to track variant changes reactively.
   */
  async #syncCartProperties() {
    const form = this.querySelector('form');
    if (!form) {
      return;
    }

    const variantId = this.refs.variantId?.value;
    if (!variantId) {
      this.#clearCartPropertyInputs();
      return;
    }

    const sellingPlanId = /** @type {HTMLInputElement | null} */ (
      form.querySelector('[name="selling_plan"]:not([data-selected-selling-plan])')
    )?.value || "";

    try {
      const cart = await getCartJson();
      const matchingItem = cart.items.find(
        (/** @type {{
           *   variant_id: number | string,
           *   properties?: Record<string, string> | Array<[string, string]>,
           *   selling_plan_allocation?: { selling_plan?: { id: string } }
           * }} */ item) => {
          const itemSellingPlanId = item?.selling_plan_allocation?.selling_plan?.id || "";
          return (
            String(item.variant_id) === String(variantId) &&
            String(itemSellingPlanId) === sellingPlanId &&
            // a Rebuy gift line is never merged into by a customer add
            !isRebuyGiftLineProperties(item.properties)
          );
        }
      );

      this.#clearCartPropertyInputs();

      if (!matchingItem?.properties) return;

      // Shopify may return properties as an array of [key, value] pairs or as an
      // object — Object.fromEntries handles both.
      const properties = Object.fromEntries(matchingItem.properties);

      for (const [key, value] of Object.entries(properties)) {
        if (value == null || value === '') continue;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = String(value);
        input.setAttribute('data-cart-property', '');
        form.appendChild(input);
      }
    } catch (error) {
      console.error('[ProductForm] Failed to sync cart properties:', error);
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
